import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Text, VStack, Flex } from '@chakra-ui/react';
import { Layout } from '../Layout/Layout';
import { motion } from 'framer-motion';
import BrokenCubesAnimation from './BrokenCubesAnimation';
import { useLocation } from 'react-router-dom';

const MotionBox = motion.create(Box);
const MotionVStack = motion.create(VStack);
const MotionText = motion.create(Text);
const MotionButton = motion.create(Button);

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundaryClass extends Component<Props & { location: any }, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    componentDidUpdate(prevProps: Props & { location: any }) {
        if (this.props.location?.pathname !== prevProps.location?.pathname) {
            this.setState({ hasError: false, error: null });
        }
    }

    public render() {
        if (this.state.hasError) {
            return (
                <Layout>
                    <MotionBox 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="center"
                        height="100%"
                        padding="32px"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        <MotionVStack 
                            spacing={4} 
                            p={8} 
                            bg="white" 
                            borderRadius="lg" 
                            boxShadow="md" 
                            width="100%" 
                            maxWidth="600px"
                            position="relative"
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ 
                                type: "spring",
                                stiffness: 100,
                                damping: 10,
                                delay: 0.2
                            }}
                        >
                            <Flex direction="column" align="center" gap={4} width="100%">
                                <Box 
                                    position="relative" 
                                    width="100px" 
                                    height="100px" 
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mb={2}
                                >
                                    <BrokenCubesAnimation />
                                </Box>
                                <MotionText 
                                    fontSize="2xl" 
                                    fontWeight="bold" 
                                    color="red.500"
                                    textAlign="center"
                                    initial={{ scale: 0.5 }}
                                    animate={{ scale: 1 }}
                                    transition={{ 
                                        type: "spring",
                                        stiffness: 200,
                                        damping: 10,
                                        delay: 0.4
                                    }}
                                >
                                    Упс! Что-то пошло не так
                                </MotionText>
                            </Flex>
                            <MotionText 
                                color="gray.600"
                                textAlign="center"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.6 }}
                            >
                                {this.state.error?.message || 'Произошла непредвиденная ошибка'}
                            </MotionText>
                            <Flex gap={4} justify="center">
                                <MotionButton 
                                    colorScheme="blue" 
                                    onClick={() => window.location.reload()}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Обновить страницу
                                </MotionButton>
                                <MotionButton 
                                    colorScheme="gray" 
                                    onClick={() => window.location.href = '/'}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    На главную
                                </MotionButton>
                            </Flex>
                        </MotionVStack>
                    </MotionBox>
                </Layout>
            );
        }

        return this.props.children;
    }
}

const ErrorBoundary = (props: Props) => {
    const location = useLocation();
    return <ErrorBoundaryClass {...props} location={location} />;
};

export default ErrorBoundary; 